import { EditOutlined } from '@ant-design/icons';
import { ColumnType, ColumnsType } from 'antd/lib/table/interface';
import { useTranslation } from 'translations/hooks';

import { SECONDARY_MAX_DECIMAL_DIGITS } from 'business/constants';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { usePermissions } from 'business/contextProviders/usePermissions';
import { useFormatMetricPoint } from 'business/production-and-performance/pages/ring-table/hooks/use-format-metric-point';
import { SteeringDataKeys } from 'business/steering/pages/table-page/components/steering-table/types';
import { SteeringDataFields } from 'business/steering/pages/table-page/types';
import { createNumberFormatter } from 'technical/format/number';
import { useMediaType } from 'technical/media/hooks';
import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';
import { notEmpty } from 'technical/utils/not-empty';
import Button from 'ui/button';

const NUMBER_CELL_ALIGN: AlignSetting = 'center';
const CELL_WIDTH = 100;

export const useSteeringTableColumns = (
  onEditionClick: (id: string | null) => void,
  isArticulated?: boolean | null,
): ColumnsType<SteeringDataFields> => {
  const { t, language } = useTranslation();
  const { isMobile } = useMediaType();
  const { currentConstructionSite } = useAppContext();

  const { formatMetricPoint } = useFormatMetricPoint();
  const integerNumberFormatter = createNumberFormatter(language, 0);

  const twoDecimalsNumberFormatter = createNumberFormatter(
    language,
    SECONDARY_MAX_DECIMAL_DIGITS,
  );

  const {
    isDataManager,
    modules: {
      STEERING: { isConstructionSiteManager },
    },
  } = usePermissions();

  const edit = (record: SteeringDataFields) => {
    onEditionClick(record.id);
  };

  const integerRenderCell = (value: any) => {
    return isUndefinedOrNull(value)
      ? '-'
      : integerNumberFormatter.format(value);
  };

  const twoDecimalsRenderCell = (value: any) => {
    return isUndefinedOrNull(value)
      ? '-'
      : twoDecimalsNumberFormatter.format(value);
  };

  const commonColumnProps = {
    align: NUMBER_CELL_ALIGN,
    width: CELL_WIDTH,
    sorter: true,
  };

  const horizontalPositionColumns = [
    {
      title: t('steering.table.columns.rear'),
      dataIndex: SteeringDataKeys.rearHorizontalPosition,
      key: SteeringDataKeys.rearHorizontalPosition,
      render: integerRenderCell,
      ...commonColumnProps,
    },
    {
      title: t('steering.table.columns.front'),
      dataIndex: SteeringDataKeys.frontHorizontalPosition,
      key: SteeringDataKeys.frontHorizontalPosition,
      render: integerRenderCell,
      ...commonColumnProps,
    },
    isArticulated
      ? {
          title: t('steering.table.columns.cuttingWheel'),
          dataIndex: SteeringDataKeys.cuttingWheelHorizontalPosition,
          key: SteeringDataKeys.cuttingWheelHorizontalPosition,
          render: integerRenderCell,
          ...commonColumnProps,
        }
      : undefined,
    {
      title: t('steering.table.columns.trend', {
        context: isArticulated ? 'rear' : undefined,
      }),
      dataIndex: SteeringDataKeys.rearHorizontalTrend,
      key: SteeringDataKeys.rearHorizontalTrend,
      render: twoDecimalsRenderCell,
      ...commonColumnProps,
    },
    isArticulated
      ? {
          title: t('steering.table.columns.trend', { context: 'front' }),
          dataIndex: SteeringDataKeys.frontHorizontalTrend,
          key: SteeringDataKeys.frontHorizontalTrend,
          render: twoDecimalsRenderCell,
          ...commonColumnProps,
        }
      : undefined,
  ].filter(notEmpty);

  const verticalPositionColumns = [
    {
      title: t('steering.table.columns.rear'),
      dataIndex: SteeringDataKeys.rearVerticalPosition,
      key: SteeringDataKeys.rearVerticalPosition,
      render: integerRenderCell,
      ...commonColumnProps,
    },
    {
      title: t('steering.table.columns.front'),
      dataIndex: SteeringDataKeys.frontVerticalPosition,
      key: SteeringDataKeys.frontVerticalPosition,
      render: integerRenderCell,
      ...commonColumnProps,
    },
    isArticulated
      ? {
          title: t('steering.table.columns.cuttingWheel'),
          dataIndex: SteeringDataKeys.cuttingWheelVerticalPosition,
          key: SteeringDataKeys.cuttingWheelVerticalPosition,
          render: integerRenderCell,
          ...commonColumnProps,
        }
      : undefined,
    {
      title: t('steering.table.columns.trend', {
        context: isArticulated ? 'rear' : undefined,
      }),
      dataIndex: SteeringDataKeys.rearVerticalTrend,
      key: SteeringDataKeys.rearVerticalTrend,
      render: twoDecimalsRenderCell,
      ...commonColumnProps,
    },
    isArticulated
      ? {
          title: t('steering.table.columns.trend', { context: 'front' }),
          dataIndex: SteeringDataKeys.frontVerticalTrend,
          key: SteeringDataKeys.frontVerticalTrend,
          render: twoDecimalsRenderCell,
          ...commonColumnProps,
        }
      : undefined,
  ].filter(notEmpty);

  const columns: ColumnsType<SteeringDataFields> = [
    {
      title: t('steering.table.columns.ringNumber'),
      dataIndex: SteeringDataKeys.ringNumber,
      key: SteeringDataKeys.ringNumber,
      align: 'center',
      width: 100,
      sorter: true,
      defaultSortOrder: 'descend',
      // Keep it visible when table is scrolled horizontally
      fixed: true,
    },
    {
      title: t('productionAndPerformance.ring.table.keyPosition'),
      dataIndex: SteeringDataKeys.keyPosition,
      key: SteeringDataKeys.keyPosition,
      render: (_, { keyPosition }) => {
        if (!keyPosition) {
          return '-';
        }
        return (currentConstructionSite?.keyPositionPrefix || '') + keyPosition;
      },
      ...commonColumnProps,
    },
    {
      title: t('productionAndPerformance.ring.table.length'),
      dataIndex: SteeringDataKeys.length,
      key: SteeringDataKeys.length,
      render: (_, { length }) => {
        if (length === 1 && currentConstructionSite?.ringLengthCode1) {
          return currentConstructionSite.ringLengthCode1;
        }
        if (length === 0 && currentConstructionSite?.ringLengthCode0) {
          return currentConstructionSite.ringLengthCode0;
        }
        return '-';
      },
      ...commonColumnProps,
    },
    {
      title: t('productionAndPerformance.ring.table.buildEndMetricPoint'),
      dataIndex: SteeringDataKeys.buildEndMetricPoint,
      key: SteeringDataKeys.buildEndMetricPoint,
      render: (_, { buildEndMetricPoint }) =>
        formatMetricPoint(buildEndMetricPoint),
      ...commonColumnProps,
    },
    {
      title: t('steering.table.columns.horizontalPosition'),
      key: 'horizontalPosition',
      children: horizontalPositionColumns,
    },
    {
      title: t('steering.table.columns.verticalPosition'),
      key: 'verticalPosition',
      children: verticalPositionColumns,
    },
    {
      title: t('steering.table.columns.skirtClearance'),
      key: 'skirtClearance',
      children: [
        {
          title: t('steering.table.columns.left'),
          dataIndex: SteeringDataKeys.leftSkirtClearance,
          key: SteeringDataKeys.leftSkirtClearance,
          render: integerRenderCell,
          ...commonColumnProps,
        },
        {
          title: t('steering.table.columns.right'),
          dataIndex: SteeringDataKeys.rightSkirtClearance,
          key: SteeringDataKeys.rightSkirtClearance,
          render: integerRenderCell,
          ...commonColumnProps,
        },
        {
          title: t('steering.table.columns.upper'),
          dataIndex: SteeringDataKeys.upperSkirtClearance,
          key: SteeringDataKeys.upperSkirtClearance,
          render: integerRenderCell,
          ...commonColumnProps,
        },
        {
          title: t('steering.table.columns.lower'),
          dataIndex: SteeringDataKeys.lowerSkirtClearance,
          key: SteeringDataKeys.lowerSkirtClearance,
          render: integerRenderCell,
          ...commonColumnProps,
        },
      ],
    },
  ];

  if (!isMobile && (isConstructionSiteManager || isDataManager)) {
    columns.push({
      title: t('productionAndPerformance.ring.table.action.title'),
      key: 'editionColumn',
      render: (_, record) => {
        return (
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => edit(record)}
          />
        );
      },
      align: 'center',
      width: '5%',
    } satisfies ColumnType<SteeringDataFields>);
  }

  return columns;
};
